// @ts-nocheck
import { useThree, useFrame } from '@react-three/fiber';
import { InstantTracker } from '@zappar/zappar-react-three-fiber/lib/index-standalone';
import React, { ReactElement, FunctionComponent, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DoubleSide, Frustum, Object3D, PerspectiveCamera, Plane, Ray, Vector3 } from 'three';
import PointerGesture, { GestureMode } from '../../../utils/pointerGesture';
import { ISceneActions, ITransCatTypes, useDataStore, useUserStore, useSceneStore, IEntityActionTypes, useActionStateStore } from '../../../zustand';
import { IActionCategory, IPlaySoundAction, IRoot, ISceneComp, ITuple3 } from '@r3f-component-data-structure';
import { ITrackingTypes } from '@r3f-component-data-structure';
import { ITransitionPhase } from '@r3f/transitions/spec';
import PlacementScene from '../PlacementScene';
import PlacementUI from '../PlacementUI/PlacementUI';
import WorldTrackingPlacementUI, { IWorldTrackingMode } from '../WorldTrackerWrapper/WorldTrackingPlacementUI/WorldTrackingPlacementUI';
import ShadowPlane from '@r3f/components/ShadowPlane';
import { isAbstractComponent } from '@r3f/utils/general';
import { useGetActiveTrackingType } from '../../hoc/hocCustomHooks';
import { useGetCurrentSceneId } from '../../../hooks/general';
import { presetAnimationController } from '../../PresetAnimationController';
import { getAllAnimationControllerOptions } from '../../../utils/action';
import { getAllOnloadModelAnimationPlayDataBySceneId } from '@r3f/utils/actions';
import { usePlacementBehaviour } from '../trackerGroupHooks';
import PlacementScenes from '../PlacementScenes';
import { Jute } from '../../..';

const PREVIEW_ORBIT_CAMERA_NAME = 'preview-orbit-camera';

/**
 * Cap how far down the ray the content may land. Near the horizon the intersection races off towards
 * infinity, and a scene parked 200m away reads as "the content vanished".
 */
const MAX_PLACEMENT_DISTANCE_M = 10;

/** How far the pointer may travel and still count as a tap rather than an orbit drag. */
const TAP_SLOP_PX = 4;

// Reused every frame; allocating a Vector3 per frame in a useFrame is how you get GC sawtooth.
const _rayOrigin = new Vector3();
const _rayDirection = new Vector3();
const _hitPoint = new Vector3();
const _ray = new Ray();
const _floorPlane = new Plane(new Vector3(0, 1, 0), 0);

type IParentProps = {
	onShowContentFirstTime?: () => any;
	enabled: boolean;
	children: ReactElement[] | undefined;
};

const WorldInstantTrackerWrapper: FunctionComponent<IParentProps> = ({ onShowContentFirstTime, enabled, children }) => {
	// Zustand
	const sceneDispatch = useSceneStore(({ dispatch }) => dispatch);
	const entityActionDispatch = useActionStateStore(({ dispatch }) => dispatch);
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	const previousSceneTransitionPhase = useSceneStore(({ transitionPhase, previousSceneId }) => transitionPhase[previousSceneId]);
	const previousSceneId = useSceneStore(({ previousSceneId }) => previousSceneId);
	const placementMode = useSceneStore(({ placementMode }) => placementMode);
	const activeTrackingType = useGetActiveTrackingType();
	const currentSceneIsNoTrackingScreen = activeTrackingType === ITrackingTypes.noTrackingScreen;
	const trackingTypeHasChanged = useSceneStore(({ trackingTypeHasChanged }) => trackingTypeHasChanged);
	const isSnapshotUiVisible = useUserStore((state) => state.isSnapshotUiVisible);
	const currentSceneId = useGetCurrentSceneId();
	const firstSceneIsWorldTracked = useDataStore((state) => {
		const firstSceneId = (state.componentsById?.[state.rootId as string] as IRoot)?.children[0];
		return (state.componentsById?.[firstSceneId] as ISceneComp)?.trackingType === 'world';
	});
	const initialPlacementHasOccuredRef = useRef(false);
	const zPlaneDepth = -5;
	const currentSceneIsWorldTracked = (componentsById?.[currentSceneId] as ISceneComp)?.trackingType === ITrackingTypes.world;
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const anchorGroupRef = useRef<Object3D | null>(null);
	/** Which scene the tracker has already made its placement decision for, so it happens once per activation. */
	const autoPlacedSceneRef = useRef<string | null>(null);
	/**
	 * Whether the content was placed at the moment world tracking was last switched off, so returning
	 * to a world scene through an image or face scene comes back placed instead of re-prompting.
	 *
	 * Named after — and behaving like — `WorldTrackerWrapper`'s ref of the same name, which is what the
	 * App Clip path has always done. Preview only: a published web experience still re-prompts.
	 */
	const isLastModePlaced = useRef(false);
	/**
	 * Whether world tracking has actually been active yet. `placementMode` starts out `false`, which is
	 * indistinguishable from "placed" — so without this, a session booting on an image scene would stash
	 * a placement that never happened and then never prompt for one.
	 */
	const hasBeenWorldTracking = useRef(false);
	/**
	 * Where the preview's anchor sits, in world space. Followed to the camera's ray while placing and
	 * frozen on Place, so the content stays where the user put it.
	 */
	const placementPointRef = useRef(new Vector3(0, 0, 0));
	/**
	 * Whether the camera's ray currently lands on the floor. Stands in for the plane-detection events
	 * the device path gets from `UserPlacementGroup`, so the preview can say "Surface found" vs
	 * "Finding surface" and show the ring only when there is something to place onto.
	 */
	const isRayOnFloorRef = useRef(false);
	const [isRayOnFloor, setIsRayOnFloor] = useState(false);
	/** The device path's plane states, derived from the ray instead of plane detection. */
	const previewPlacementUiMode: IWorldTrackingMode = !placementMode ? 'placed' : isRayOnFloor ? 'intersecting' : 'searching';

	const previousSceneTrackingTypeRef = useRef<ITrackingTypes>();
	previousSceneTrackingTypeRef.current = (componentsById?.[previousSceneId] as ISceneComp)?.trackingType;

	// Ref
	const initialPlacementCameraOffset: ITuple3 = [0, -0.75, zPlaneDepth];
	const placementCameraOffsetRef = useRef<ITuple3>(initialPlacementCameraOffset);

	// State
	const [groupScale, setGroupScale] = useState<ITuple3>([1, 1, 1]); // Scale applied to placement scene
	const [activeGesture, setActiveGesture] = useState(false); // Whether or not there is an active gesture ( drag / pinch )
	const [pointerGestureHandler, setPointerGestureHandler] = useState<undefined | PointerGesture>(undefined);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_, setRefresh] = useState(0); // To force a refresh when dragging
    const {enterPlacementBehaviour, exitPlacementBehaviour} = usePlacementBehaviour()

	// Hotspot setup
	const { camera, scene, gl } = useThree();
	const geometry = useMemo(() => <planeGeometry args={[50, 50]} />, []);
	const hotspotPlaneRef = useRef<Object3D>();

	const sceneComp = useMemo(() => componentsById?.[currentSceneId], [currentSceneId, componentsById]) as ISceneComp;
    const sceneOnLoadPlaySoundActions = useMemo(() => sceneComp?.actions?.onLoad?.filter(action => action.type === IActionCategory.playSound), [sceneComp]) as IPlaySoundAction[];

	// Make the plane camera relative
	useFrame(() => {
		if (hotspotPlaneRef.current && hotspotPlaneRef.current.parent !== camera) {
			camera.add(hotspotPlaneRef.current);
		}
	});

	/** GESTURE HANDLERS */
	const onGesturePinchHandler = useCallback((currentDiff: number) => {
		setGroupScale([currentDiff, currentDiff, currentDiff]);
	}, []);

	const onGestureDragHandler = useCallback(
		(delta: number[]) => {
			const frustum = new Frustum().setFromProjectionMatrix(camera.projectionMatrix);
			let prospectivePoint = new Vector3((placementCameraOffsetRef.current[0] || 0) + delta[0], (placementCameraOffsetRef.current[1] || 0) + delta[1], zPlaneDepth);
			// Don't allow dragging the anchor outside of camera view
			if (!frustum.containsPoint(prospectivePoint)) return;
			placementCameraOffsetRef.current = [prospectivePoint.x, prospectivePoint.y, prospectivePoint.z];
			setRefresh((prevState) => prevState + 1);
		},
		[zPlaneDepth, camera.projectionMatrix]
	);

	const onGestureMoveHandler = useCallback((mode: GestureMode) => {
		if (mode !== null) {
			setActiveGesture(true);
		} else {
			setActiveGesture(false);
		}
	}, []);

	const onGestureUpHandler = useCallback(() => {
		setActiveGesture(false);
	}, []);

	/**
	 * Follow the camera's aim onto the floor, the way pointing a device at a surface does.
	 *
	 * The orbit camera always looks at the OrbitControls target, so orbiting and zooming sweep this
	 * intersection across the floor with no animation of our own. A ray at or above the horizon has no
	 * hit — keep the last point rather than snapping, or a glance upwards flings the content away.
	 */
	const trackPlacementPointToFloor = useCallback((): boolean => {
		// BY NAME, never `useThree().camera`: on a Zappar camera `near`/`far` alias its projection
		// inputs, and it is not the camera that renders a world preview scene anyway.
		const previewCamera = scene.getObjectByName(PREVIEW_ORBIT_CAMERA_NAME) as PerspectiveCamera | undefined;
		if (!previewCamera) return false;

		previewCamera.getWorldPosition(_rayOrigin);
		previewCamera.getWorldDirection(_rayDirection);
		_ray.set(_rayOrigin, _rayDirection);

		const hit = _ray.intersectPlane(_floorPlane, _hitPoint);
		if (!hit) return false;
		if (_rayOrigin.distanceTo(hit) > MAX_PLACEMENT_DISTANCE_M) return false;

		// Keep the last good point when the ray misses, so a glance at the horizon holds the scene
		// still rather than flinging it away — the caller reports "searching" instead.
		placementPointRef.current.copy(hit);
		return true;
	}, [scene]);

	/**
	 * Drive the anchor from the preview's own camera instead of the tracker.
	 *
	 * `InstantWorldAnchorGroup` sets `matrixAutoUpdate = false` and overrides `updateMatrixWorld` to
	 * rewrite its matrix from `anchor.pose(zapparCamera.rawPose)` on EVERY frame, so writing
	 * `position` or `matrix` from out here is discarded on the next render, and `enabled={false}` only
	 * stops the CV tracker updating the pose, not the group. Dropping the override back to the base
	 * implementation is the one thing that makes our own matrix stick — otherwise content sits at the
	 * placement offset, ~5m in front of a camera that no longer renders.
	 *
	 * Asserted every frame rather than once in an effect. `InstantTracker` builds a new ref callback on
	 * every render (`mergeRefs`), so React nulls this ref and re-sets it whenever that component
	 * re-renders — which happens each time placement mode toggles. A one-shot effect keyed on
	 * `anchorGroupRef.current` cannot see that (a ref is not a dependency), so its cleanup dropped the
	 * override with nothing to put it back. Same reasoning as PreviewBackground asserting the
	 * background every frame.
	 */
	useFrame(() => {
		const group = anchorGroupRef.current;
		if (!Jute.isPreviewSimulator || !group) return;

		if (group.updateMatrixWorld !== Object3D.prototype.updateMatrixWorld) {
			group.updateMatrixWorld = Object3D.prototype.updateMatrixWorld;
		}

		if (useSceneStore.getState().placementMode) {
			const hit = trackPlacementPointToFloor();
			// Only on a flip — a setState every frame would re-render the whole tracker group 60x/sec.
			if (hit !== isRayOnFloorRef.current) {
				isRayOnFloorRef.current = hit;
				setIsRayOnFloor(hit);
			}
		}
		group.matrix.makeTranslation(placementPointRef.current.x, placementPointRef.current.y, placementPointRef.current.z);
		// `matrixAutoUpdate` is false on this group, and the base `updateMatrixWorld` recomputes
		// `matrixWorld` only when this flag is set — clearing it after the first pass. Writing `matrix`
		// without it would move nothing from the second frame onwards.
		group.matrixWorldNeedsUpdate = true;
	});

	// Effects
	useLayoutEffect(() => {
		const pgh = new PointerGesture(camera);
		setPointerGestureHandler(pgh);
	}, [camera]);

	useLayoutEffect(() => {
		if (pointerGestureHandler) {
			pointerGestureHandler.onDragHandler = onGestureDragHandler;
			pointerGestureHandler.onPinchHandler = onGesturePinchHandler;
			pointerGestureHandler.onPointerMoveHandler = onGestureMoveHandler;
			pointerGestureHandler.onPointerUpHandler = onGestureUpHandler;
		}
	}, [onGestureDragHandler, onGesturePinchHandler, onGestureMoveHandler, onGestureUpHandler, pointerGestureHandler]);

	// Required so that the pointer gestures ( drag, pinch ) don't persist after the scene is placed
	useEffect(() => {
		if (!placementMode && pointerGestureHandler?.isActive) {
			pointerGestureHandler?._cleanup();
		}
	}, [placementMode, pointerGestureHandler]);

	// Always the latest closure, so the pointer listeners below can stay bound across re-renders.
	const exitPlacementBehaviourRef = useRef(exitPlacementBehaviour);
	exitPlacementBehaviourRef.current = exitPlacementBehaviour;

	/**
	 * Tap the canvas to place, drag to aim.
	 *
	 * The device UI places on a pointerdown anywhere, which cannot work here: the drag that aims the
	 * camera would place the scene on its first frame. So we measure the gesture and only place if the
	 * pointer barely moved, and only while the ray is actually on the floor — you cannot place onto a
	 * surface that was not found.
	 */
	useEffect(() => {
		if (!Jute.isPreviewSimulator || !placementMode || !enabled) return;

		const canvas = gl.domElement;
		let downX = 0;
		let downY = 0;

		const onPointerDown = (e: PointerEvent) => {
			downX = e.clientX;
			downY = e.clientY;
		};
		const onPointerUp = (e: PointerEvent) => {
			if (Math.hypot(e.clientX - downX, e.clientY - downY) > TAP_SLOP_PX) return;
			if (!isRayOnFloorRef.current) return;
			exitPlacementBehaviourRef.current();
		};

		canvas.addEventListener('pointerdown', onPointerDown);
		canvas.addEventListener('pointerup', onPointerUp);
		return () => {
			canvas.removeEventListener('pointerdown', onPointerDown);
			canvas.removeEventListener('pointerup', onPointerUp);
		};
		// Deliberately NOT keyed on `exitPlacementBehaviour`: the hook hands back a new closure every
		// render, and the preview re-renders on every keystroke of an edit. Re-binding mid-gesture would
		// throw away the pointerdown coordinates, so the pointerup would measure against 0,0, read as a
		// drag, and silently refuse to place. The ref keeps the callback current instead.
	}, [placementMode, enabled, gl]);

	// global placement state is false initially to allow pointer events if active scene is image tracked.
	useEffect(() => {
		if (Jute.isPreviewSimulator) {
			// `enabled` as well as the tracking type, matching the device branch below. It only flips at
			// the previous scene's exitFinish, so without it placement starts mid-exit against a tracker
			// that is still off. And placementMode is GLOBAL: leaving a world scene unplaced used to
			// carry it onto the next one, where EntityLevelFadeGroup hides every entity and
			// useHocPointerDown swallows every tap.
			if (!enabled || !currentSceneIsWorldTracked) {
				// Read from the store, not the render closure: this effect does not depend on
				// `placementMode`, so the captured value can be a frame behind the dispatch below.
				isLastModePlaced.current = hasBeenWorldTracking.current && !useSceneStore.getState().placementMode;
				autoPlacedSceneRef.current = null;
				sceneDispatch?.({
					type: ISceneActions.SET_PLACEMENT_MODE,
					placementMode: false,
				});
				return;
			}
			hasBeenWorldTracking.current = true;
			// ONCE per activation of this scene. This effect re-runs on several unrelated dependencies,
			// and neither placement behaviour is idempotent — entering resets animations, exiting
			// dispatches an enter transition and re-triggers onLoad sound actions every time it is called.
			const isTrackerReactivation = autoPlacedSceneRef.current === null;
			if (autoPlacedSceneRef.current === currentSceneId) return;
			autoPlacedSceneRef.current = currentSceneId;

			// A world scene reached from another world scene leaves placement alone entirely, the way the
			// device path below does — the content is already sitting where the user put it, and the link
			// action has already dispatched that scene's enter transition.
			if (!isTrackerReactivation) return;

			// Placed once, placed for the session: coming back to world tracking through an image or face
			// scene restores the last placement rather than asking again.
			if (isLastModePlaced.current) {
				exitPlacementBehaviour();
				return;
			}

			// The preview starts UNPLACED and stays that way until the user presses Place. There is no
			// device to aim, so the camera's own ray onto the floor stands in for pointing a phone at a
			// surface — see `trackPlacementPointToFloor`.
			enterPlacementBehaviour();
			return;
		}

		// if current scene is not world trackend or world tracking isn't enabeld hide placement ui
		if (!enabled || !currentSceneIsWorldTracked) {
			isLastModePlaced.current = hasBeenWorldTracking.current && !useSceneStore.getState().placementMode;
			autoPlacedSceneRef.current = null;
			sceneDispatch?.({
				type: ISceneActions.SET_PLACEMENT_MODE,
				placementMode: false,
			});
			return;
		}

		hasBeenWorldTracking.current = true;

		// Prevents emtpy placement ui being shown for menu scenes
		if (currentSceneIsNoTrackingScreen) return;

		// show placement ui if
		// 1) its the first load of the first scene (tracking type hasn't changed & no previous transition phase)
		// 2) tracking type has changed and the previous exit scene has finished.
		if ((!trackingTypeHasChanged && !previousSceneTransitionPhase) || (trackingTypeHasChanged && previousSceneTransitionPhase === ITransitionPhase.exitFinish)) {
			// Placed once, placed for the session — the same rule as the preview branch above, and as
			// `WorldTrackerWrapper` on the App Clip. Preview only: a published experience still re-prompts
			// on every change of tracking type, which is the behaviour those experiences ship with.
			if (Jute.isPreview && isLastModePlaced.current) {
				// Unlike the `placementMode: true` dispatches below, this is not idempotent — and nothing
				// else in this branch stops the effect re-running for a scene it has already handled.
				if (autoPlacedSceneRef.current === currentSceneId) return;
				autoPlacedSceneRef.current = currentSceneId;
				exitPlacementBehaviour();
				return;
			}
			// if switch from face tracking to world add delay to await camera switch
			if (trackingTypeHasChanged && previousSceneTrackingTypeRef.current === ITrackingTypes.face) {
				if (timeoutRef.curret) clearTimeout(timeoutRef.current);
				timeoutRef.current = setTimeout(() => {
					sceneDispatch?.({
						type: ISceneActions.SET_PLACEMENT_MODE,
						placementMode: true,
					});
				}, 500);
				return;
			}
			sceneDispatch?.({
				type: ISceneActions.SET_PLACEMENT_MODE,
				placementMode: true,
			});
		}
		// `currentSceneId` so that moving between two WORLD scenes re-runs this: none of the other
		// dependencies necessarily change on that transition, and the new scene would never get placed.
	}, [sceneDispatch, currentSceneIsNoTrackingScreen, enabled, previousSceneTransitionPhase, currentSceneIsWorldTracked, trackingTypeHasChanged, currentSceneId]);

	return (
		// show placement ui and placement scene if tracking is enabled, show prompt is true
		// (i.e. tracking has changed/or is on initial first scene) and in placement mode
		<>
			{/* The preview borrows the world tracker's placement UI rather than the instant tracker's bare
			    button, so it reads like real world tracking: "Finding surface" until the camera's ray
			    lands on the floor, then "Surface found". Placement itself is driven by the tap-vs-drag
			    listener above, hence `placeOnDocumentTap={false}`. */}
			{!isSnapshotUiVisible && Jute.isPreviewSimulator && (
				<WorldTrackingPlacementUI
					enabled={enabled}
					mode={previewPlacementUiMode}
					placeOnDocumentTap={false}
					onPlace={() => exitPlacementBehaviour()}
					onReset={() => {
						enterPlacementBehaviour();
						setGroupScale([1, 1, 1]);
					}}
				/>
			)}
			{!isSnapshotUiVisible && !Jute.isPreviewSimulator && (
				<PlacementUI
					activeGesture={activeGesture}
					enabled={enabled}
					onPlacementModeChange={() => {
						if (!placementMode) {
							enterPlacementBehaviour();
							// Reset scale of group
							setGroupScale([1, 1, 1]);
							// Reset camera position offset
							placementCameraOffsetRef.current = initialPlacementCameraOffset;
							return;
						} 
						// if first scene is world tracked, world tracked content has never been placed before
						// and placement mode is changed from true to false (at this point still true)
						// the experience content (not placement ui) is shown for first time
						if (!initialPlacementHasOccuredRef.current && placementMode && firstSceneIsWorldTracked) {
							onShowContentFirstTime?.();
						}
						initialPlacementHasOccuredRef.current = true;
						
						exitPlacementBehaviour();
					}}
				/>
			)}
			{/* HOTSPOT FOR DRAGGING */}
			{placementMode && (
				<mesh
					position={[0, 0, zPlaneDepth]}
					visible={false}
					onPointerDown={(e) => pointerGestureHandler?._pointerDownHandler(e)}
					onPointerMove={(e) => pointerGestureHandler?._pointerMoveHandler(e)}
					onPointerUp={(e) => pointerGestureHandler?._pointerUpHandler(e)}
					onPointerCancel={() => pointerGestureHandler?._cleanup()}
					ref={hotspotPlaneRef}
				>
					{geometry}
					<meshBasicMaterial opacity={0} transparent={true} />
				</mesh>
			)}
			{/* END HOTSPOT FOR DRAGGING */}
			{
				<InstantTracker ref={anchorGroupRef} enabled={enabled} name="world_tracker_group" placementMode={placementMode} placementCameraOffset={placementCameraOffsetRef.current}>
					<group scale={groupScale}>
						<group visible={enabled && placementMode}>
							<PlacementScenes />
						</group>
						<group visible={!enabled || !placementMode}>{children}</group>
						{!placementMode && enabled && <ShadowPlane rotation={[-Math.PI / 2, 0, 0]} occlusion opacity={0.3} />}
					</group>
					{/* Left OUTSIDE the scale group deliberately, unlike WorldTrackerWrapper's: here that group
					    carries the pinch-to-scale factor, and the reticle should not scale with the content.
					    In the preview it shows only while the ray is on the floor, matching the device path
					    showing it only while a plane is intersected. */}
					{placementMode && (!Jute.isPreviewSimulator || isRayOnFloor) && (
						<mesh position={[0, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
							<ringGeometry args={[0.25, 0.28, 50]} />
							<meshBasicMaterial color={0xffffff} side={DoubleSide} />
						</mesh>
					)}
				</InstantTracker>
			}
		</>
	);
};

WorldInstantTrackerWrapper.displayName = 'WorldInstantTrackerWrapper';
export default memo(WorldInstantTrackerWrapper);
